"use client";

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { AnimatePresence, motion, type Variants } from "motion/react";
import { X } from "lucide-react";
import BasicPagination from "@/components/common/BasicPagination";
import { Skeleton } from "@/components/ui/skeleton";
import InsuranceProviderItem from "@/components/resource/insurance/InsuranceProviderItem";
import type { InsuranceType } from "@/lib/data/insurance";
import {
  convertInsuranceSearchParamsToQueryKey,
  getInsuranceSearchUrlQuery,
  INSURANCE_DEFAULT_SORT,
  type InsuranceProviderCardItem,
  type InsuranceSearchParams,
} from "@/lib/fetch/insurance";
import InsuranceSearchForm from "./InsuranceSearchForm";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.01, delayChildren: 0 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.25, ease: "easeOut" },
  },
};

type Props = {
  params: InsuranceSearchParams;
  typeCounts: Record<InsuranceType, number>;
};

/** Provider search: filters, the active-filter row, the card grid and URL sync. */
const InsuranceSearchView: React.FC<Props> = ({ params, typeCounts }) => {
  const t = useTranslations("insurance");
  const [form, setForm] = useState<InsuranceSearchParams>({ ...params });

  useEffect(() => {
    const nextSearch = getInsuranceSearchUrlQuery(form).toString();
    const currentSearch =
      typeof window !== "undefined" ? window.location.search.slice(1) : "";

    if (nextSearch !== currentSearch) {
      const nextUrl = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
      }`;
      window.history.replaceState(null, "", nextUrl);
    }
  }, [form]);

  const query = useQuery({
    queryKey: [
      "resource",
      "insurance",
      ...convertInsuranceSearchParamsToQueryKey(form),
    ],
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const search = getInsuranceSearchUrlQuery(form);
      search.set("language", form.language);
      const res = await fetch(
        `/api/site/public/resource/insurance/search?${search}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
      );

      if (!res.ok) {
        toast.error(t("search.error-fetching"));
        return { items: [] as InsuranceProviderCardItem[], count: 0 };
      }

      const result = (await res.json()) as {
        success: boolean;
        data: { items: InsuranceProviderCardItem[]; count: number };
      };
      return { items: result.data.items, count: result.data.count };
    },
  });

  const loading = query.isLoading || query.isFetching;
  const total = query.data?.count ?? 0;
  const activeSearch = form.search?.trim() ?? "";
  const hasFilters = activeSearch.length > 0 || form.type !== "ALL";

  return (
    <div className="flex w-full flex-col gap-6">
      <InsuranceSearchForm
        form={form}
        setForm={(next) => setForm({ ...next, page: 1 })}
        typeCounts={typeCounts}
      />

      <div className="flex w-full flex-col gap-4">
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-3">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="meta-loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Skeleton className="h-5 w-52" />
              </motion.div>
            ) : (
              <motion.p
                key="meta-ready"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-sm text-muted-foreground"
              >
                {t("search.showing-x-of-y", {
                  total,
                  from: total === 0 ? 0 : (form.page - 1) * form.pageSize + 1,
                  to: Math.min(form.page * form.pageSize, total),
                })}
              </motion.p>
            )}
          </AnimatePresence>

          {hasFilters && (
            <div className="flex flex-wrap items-center gap-2">
              {form.type !== "ALL" && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, type: "ALL", page: 1 })}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-full bg-secondary px-3 text-xs font-semibold text-secondary-foreground transition-opacity hover:opacity-80"
                >
                  {t(`type.${form.type}`)}
                  <X className="size-3" />
                </button>
              )}
              {activeSearch && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, search: "", page: 1 })}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-semibold transition-colors hover:border-zinc-300"
                >
                  {t("search.active.query", { query: activeSearch })}
                  <X className="size-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    search: "",
                    type: "ALL",
                    sort: INSURANCE_DEFAULT_SORT,
                    page: 1,
                  })
                }
                className="text-xs font-semibold text-blue-700 hover:underline"
              >
                {t("search.active.clear-all")}
              </button>
            </div>
          )}
        </div>

        {(query.data && query.data.items.length > 0) || loading ? (
          <>
            <motion.div
              key={`items-container-${loading}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
            >
              <AnimatePresence>
                {loading
                  ? new Array(Math.min(form.pageSize, 12))
                      .fill(0)
                      .map((_, index) => (
                        <motion.div
                          key={`insurance-skeleton-${index}`}
                          variants={itemVariants}
                        >
                          <Skeleton className="h-64 w-full rounded-2xl" />
                        </motion.div>
                      ))
                  : query.data?.items.map((item) => (
                      <motion.div
                        variants={itemVariants}
                        exit={{ opacity: 0, scale: 0.95 }}
                        key={item.slug}
                      >
                        <InsuranceProviderItem item={item} />
                      </motion.div>
                    ))}
              </AnimatePresence>
            </motion.div>

            <div className="min-h-16">
              {query.data && total > form.pageSize && !loading && (
                <BasicPagination
                  currentPage={form.page}
                  perPage={form.pageSize}
                  totalPages={Math.ceil(total / form.pageSize)}
                  onPageChange={(page) => setForm({ ...form, page })}
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border px-6 text-center">
            <p className="text-sm text-muted-foreground">
              {t("search.not-found")}
            </p>
            {hasFilters && (
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    search: "",
                    type: "ALL",
                    sort: INSURANCE_DEFAULT_SORT,
                    page: 1,
                  })
                }
                className="text-sm font-semibold text-blue-700 hover:underline"
              >
                {t("search.active.clear-all")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsuranceSearchView;
